import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Columns3 } from 'lucide-react';

// All available master-data fields users can add to the list view.
export const OPTIONAL_COLUMNS = [
  'thickness',
  'category',
  'created_at',
  'manufacturer_part_number',
  'material_grade',
  'core_diameter',
  'material_index',
  'common_description',
  'number_of_rolls',
  'is_special',
];

export const ColumnPickerButton = ({ optionalCols, onToggle, onClear, className }) => {
  const { t } = useTranslation();
  const columns = [
    { id: 'thickness', label: t('list.col.thickness', 'Stärke (µm)') },
    { id: 'category', label: t('list.col.category', 'Kategorie') },
    { id: 'created_at', label: t('list.col.createdAt', 'Erstelldatum') },
    { id: 'manufacturer_part_number', label: t('list.col.partNumber', 'Hersteller-Art.-Nr.') },
    { id: 'material_grade', label: t('list.col.grade', 'Qualität / Grade') },
    { id: 'core_diameter', label: t('list.col.coreDiameter', 'Hülsen-Ø (mm)') },
    { id: 'material_index', label: t('list.col.materialIndex', 'Material-Index') },
    { id: 'common_description', label: t('list.col.description', 'Beschreibung') },
    { id: 'number_of_rolls', label: t('list.col.numberOfRolls', 'Rollen') },
    { id: 'is_special', label: t('list.col.special', 'Spezial') },
  ];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={className || 'rounded-none border-slate-300 dark:border-slate-600 h-9 gap-2'}
          data-testid="column-picker-btn"
        >
          <Columns3 className="h-4 w-4" />
          <span className="hidden sm:inline text-sm">{t('list.columns', 'Spalten')}</span>
          {optionalCols.length > 0 && (
            <span className="text-[10px] bg-orange-500 text-white rounded-full px-1.5 py-0.5 leading-none">
              +{optionalCols.length}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 p-3 rounded-none border-slate-300 dark:border-slate-600 max-h-[70vh] overflow-y-auto"
        data-testid="column-picker-popover"
      >
        <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
          {t('list.optionalColumns', 'Zusätzliche Spalten')}
        </p>
        <div className="space-y-1.5">
          {columns.map((col) => (
            <Label
              key={col.id}
              className="flex items-center gap-2 cursor-pointer text-sm font-normal"
              data-testid={`column-picker-${col.id}-label`}
            >
              <Checkbox
                checked={optionalCols.includes(col.id)}
                onCheckedChange={() => onToggle(col.id)}
                data-testid={`column-picker-${col.id}`}
              />
              <span>{col.label}</span>
            </Label>
          ))}
        </div>
        {optionalCols.length > 0 && (
          <button
            type="button"
            onClick={onClear}
            className="mt-3 w-full text-[11px] uppercase tracking-wider text-slate-500 hover:text-orange-500 transition-colors border-t border-slate-200 dark:border-slate-700 pt-2"
            data-testid="column-picker-clear-btn"
          >
            {t('list.clearColumns', 'Alle abwählen')}
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
};
